// =============================================================================
// DATA MODELER TYPES
// =============================================================================

export type PrimitiveType =
  | 'person'
  | 'organization'
  | 'document'
  | 'asset'
  | 'transaction'
  | 'workflow'
  | 'event'
  | 'location';

export interface Primitive {
  id: string;
  type: PrimitiveType;
  name: string;
  icon: string;
  description: string;
  color: string;
}

export type FieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'email'
  | 'phone'
  | 'url'
  | 'json'
  | 'file'
  | 'enum'
  | 'relation';

export interface EntityField {
  id: string;
  name: string;
  type: FieldType;
  required: boolean;
  unique?: boolean;
  indexed?: boolean;
  defaultValue?: string | number | boolean;
  description?: string;
  relationTarget?: string;
  enumValues?: string[];
  isPrimitive?: boolean;
}

export interface Entity {
  id: string;
  name: string;
  primitive: PrimitiveType;
  fields: EntityField[];
  position: { x: number; y: number };
  color: string;
}

export type RelationType = 'one-to-one' | 'one-to-many' | 'many-to-one' | 'many-to-many';

export interface Relation {
  id: string;
  name: string;
  sourceEntityId: string;
  targetEntityId: string;
  sourceField?: string;
  targetField?: string;
  type: RelationType;
  foreignKey?: string;
  required?: boolean;
  cascadeDelete?: boolean;
}

export interface DataModel {
  id: string;
  name: string;
  description: string | undefined;
  entities: Entity[];
  relations: Relation[];
  createdAt: string;
  updatedAt: string;
}

// Primitives catalog
export const PRIMITIVES: Primitive[] = [
  {
    id: 'person',
    type: 'person',
    name: 'Person',
    icon: 'User',
    description: 'Individual or user entity',
    color: '#3B82F6',
  },
  {
    id: 'organization',
    type: 'organization',
    name: 'Organization',
    icon: 'Building2',
    description: 'Company or organization entity',
    color: '#8B5CF6',
  },
  {
    id: 'document',
    type: 'document',
    name: 'Document',
    icon: 'FileText',
    description: 'Document or file entity',
    color: '#10B981',
  },
  {
    id: 'asset',
    type: 'asset',
    name: 'Asset',
    icon: 'Package',
    description: 'Physical or digital asset',
    color: '#F59E0B',
  },
  {
    id: 'transaction',
    type: 'transaction',
    name: 'Transaction',
    icon: 'ArrowRightLeft',
    description: 'Financial or data transaction',
    color: '#EF4444',
  },
  {
    id: 'workflow',
    type: 'workflow',
    name: 'Workflow',
    icon: 'GitBranch',
    description: 'Process or workflow entity',
    color: '#06B6D4',
  },
  {
    id: 'event',
    type: 'event',
    name: 'Event',
    icon: 'Calendar',
    description: 'Time-based event entity',
    color: '#EC4899',
  },
  {
    id: 'location',
    type: 'location',
    name: 'Location',
    icon: 'MapPin',
    description: 'Geographic location entity',
    color: '#84CC16',
  },
];

// Default fields per primitive type
export const DEFAULT_FIELDS: Record<PrimitiveType, Omit<EntityField, 'id'>[]> = {
  person: [
    { name: 'firstName', type: 'string', required: true, isPrimitive: true },
    { name: 'lastName', type: 'string', required: true, isPrimitive: true },
    { name: 'email', type: 'email', required: true, unique: true, isPrimitive: true },
    { name: 'phone', type: 'phone', required: false, isPrimitive: true },
  ],
  organization: [
    { name: 'name', type: 'string', required: true, isPrimitive: true },
    { name: 'industry', type: 'string', required: false, isPrimitive: true },
    { name: 'website', type: 'url', required: false, isPrimitive: true },
    { name: 'size', type: 'enum', required: false, enumValues: ['small', 'medium', 'large', 'enterprise'], isPrimitive: true },
  ],
  document: [
    { name: 'title', type: 'string', required: true, isPrimitive: true },
    { name: 'content', type: 'string', required: false, isPrimitive: true },
    { name: 'fileUrl', type: 'file', required: false, isPrimitive: true },
    { name: 'status', type: 'enum', required: true, enumValues: ['draft', 'published', 'archived'], isPrimitive: true },
  ],
  asset: [
    { name: 'name', type: 'string', required: true, isPrimitive: true },
    { name: 'value', type: 'number', required: false, isPrimitive: true },
    { name: 'serialNumber', type: 'string', required: false, unique: true, isPrimitive: true },
    { name: 'acquisitionDate', type: 'date', required: false, isPrimitive: true },
  ],
  transaction: [
    { name: 'amount', type: 'number', required: true, isPrimitive: true },
    { name: 'currency', type: 'string', required: true, defaultValue: 'EUR', isPrimitive: true },
    { name: 'status', type: 'enum', required: true, enumValues: ['pending', 'completed', 'failed', 'refunded'], isPrimitive: true },
    { name: 'transactionDate', type: 'datetime', required: true, isPrimitive: true },
  ],
  workflow: [
    { name: 'name', type: 'string', required: true, isPrimitive: true },
    { name: 'status', type: 'enum', required: true, enumValues: ['draft', 'active', 'paused', 'completed'], isPrimitive: true },
    { name: 'currentStep', type: 'string', required: false, isPrimitive: true },
    { name: 'startedAt', type: 'datetime', required: false, isPrimitive: true },
  ],
  event: [
    { name: 'title', type: 'string', required: true, isPrimitive: true },
    { name: 'startDate', type: 'datetime', required: true, isPrimitive: true },
    { name: 'endDate', type: 'datetime', required: false, isPrimitive: true },
    { name: 'location', type: 'string', required: false, isPrimitive: true },
  ],
  location: [
    { name: 'name', type: 'string', required: true, isPrimitive: true },
    { name: 'address', type: 'string', required: false, isPrimitive: true },
    { name: 'latitude', type: 'number', required: false, isPrimitive: true },
    { name: 'longitude', type: 'number', required: false, isPrimitive: true },
  ],
};
